import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Plus, Trash2, Pencil } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const empty = { number: "", capacity: 4, location: "" };

export default function AdminTables() {
  const { user } = useAuth();
  const expired = user?.subscription_active === false;
  const [tables, setTables] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/tables");
      setTables(data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm(empty); setOpen(true); };
  const startEdit = (t) => { setEditing(t); setForm({ number: t.number, capacity: t.capacity, location: t.location || "" }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form, number: parseInt(form.number, 10), capacity: parseInt(form.capacity, 10) };
      if (editing) {
        await api.put(`/admin/tables/${editing.id}`, payload);
        toast.success("Mesa actualizada");
      } else {
        await api.post("/admin/tables", payload);
        toast.success("Mesa creada");
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => {
    try { await api.delete(`/admin/tables/${id}`); toast.success("Mesa eliminada"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div className="animate-fade-up" data-testid="admin-tables-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#7248ae] mb-2">Setup</p>
          <h1 className="font-display text-4xl sm:text-5xl font-black text-[#1a0f2e] tracking-tight">Mesas</h1>
          <p className="text-[#7248ae] mt-2">Da de alta nuevas mesas con su capacidad y ubicación.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={startCreate} disabled={expired} className="bg-[#3e166c] hover:bg-[#7248ae] text-white rounded-xl" data-testid="new-table-button">
              <Plus className="h-4 w-4 mr-2" />Nueva mesa
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display text-2xl">{editing ? "Editar mesa" : "Nueva mesa"}</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4" data-testid="table-form">
              <div><Label>Número</Label><Input type="number" min={1} required value={form.number} onChange={e=>setForm({...form,number:e.target.value})} data-testid="table-number-input" /></div>
              <div><Label>Capacidad (personas)</Label><Input type="number" min={1} required value={form.capacity} onChange={e=>setForm({...form,capacity:e.target.value})} data-testid="table-capacity-input" /></div>
              <div><Label>Ubicación</Label><Input value={form.location} onChange={e=>setForm({...form,location:e.target.value})} placeholder="Salón, Terraza, …" data-testid="table-location-input" /></div>
              <DialogFooter><Button type="submit" className="bg-[#3e166c] hover:bg-[#7248ae] text-white" data-testid="submit-table-button">{editing ? "Guardar" : "Crear"}</Button></DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4" data-testid="tables-grid">
        {tables.map((t, i) => (
          <Card key={t.id} className={`p-5 border border-[#b182ec]/30 rounded-2xl card-glow transition-all hover:-translate-y-1 animate-fade-up stagger-${(i%6)+1}`} data-testid={`table-card-${t.number}`}>
            <p className="text-xs uppercase tracking-wider text-[#7248ae]">Mesa</p>
            <p className="font-display text-4xl font-black text-[#1a0f2e]">{t.number}</p>
            <p className="text-sm text-[#7248ae] mt-1">{t.capacity} pers.</p>
            {t.location && <p className="text-xs text-[#7248ae]">{t.location}</p>}
            <div className="flex gap-2 mt-4">
              <Button size="sm" variant="outline" onClick={() => startEdit(t)} disabled={expired} className="border-[#b182ec]/50" data-testid={`edit-table-${t.number}`}>
                <Pencil className="h-3 w-3" />
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button size="sm" variant="outline" disabled={expired} className="border-[#e11d48]/40 text-[#e11d48] hover:bg-[#e11d48]/10" data-testid={`delete-table-${t.number}`}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Eliminar mesa {t.number}?</AlertDialogTitle>
                    <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancelar</AlertDialogCancel>
                    <AlertDialogAction className="bg-[#e11d48] hover:bg-[#e11d48]/80" onClick={() => remove(t.id)}>Eliminar</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
